import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { GpdButtonComponent } from '../../../../../components/gpd-button/gpd-button.component';
import { DxSortableComponent } from 'devextreme-angular/ui/sortable';
import { DxScrollViewComponent, DxSelectBoxModule, DxValidationGroupComponent } from 'devextreme-angular';
import { DragChangeEvent, ReorderEvent } from 'devextreme/ui/sortable';
import { DxValidatorModule } from 'devextreme-angular/ui/validator';
import { MilestoneProjectConfigNodeComponent } from '../components/project-config-node/types/milestone-project-config-node/milestone-project-config-node.component';
import { PhaseProjectConfigNodeComponent } from '../components/project-config-node/types/phase-project-config-node/phase-project-config-node.component';
import { TaskPackageProjectConfigNodeComponent } from '../components/project-config-node/types/task-package-project-config-node/task-package-project-config-node.component';
import { Milestone, Phase, TaskPackage } from '../../../../../interfaces/entities/project-configuration';
import { SharedPipesModule } from '../../../../../pipes/shared.pipes.module';
import { ConfigService } from '../../../../../services/config.service';
import { AppGlobals } from '../../../../../app.globals';
import { GenericPopupComponent, PopupButton } from 'src/app/components/generic-popup/generic-popup.component';
import { ValidationToastComponent } from 'src/app/components/validation-toast/validation-toast.component';

import { SearchableDropdownDirective } from '../../../../../directives/searchable-dropdown.directive';
interface EditState {
  type: 'phase' | 'milestone' | 'task-package';
  editingIndex: number;
  isNew?: boolean;
}

@Component({
    selector: 'app-project-config-overview',
    imports: [
        CommonModule,
        GpdButtonComponent,
        PhaseProjectConfigNodeComponent,
        MilestoneProjectConfigNodeComponent,
        TaskPackageProjectConfigNodeComponent,
        DxSortableComponent,
        DxScrollViewComponent,
        DxValidationGroupComponent,
        DxValidatorModule,
        SharedPipesModule,
        DxSelectBoxModule,
        GenericPopupComponent,
        ValidationToastComponent,
        SearchableDropdownDirective,
    ],
    templateUrl: './project-config-overview.component.html',
    styleUrl: './project-config-overview.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectConfigOverviewComponent {
  phases: Phase[] = [];
  isLoading: boolean = false;
  loadError: string | null = null;

  selectedPhaseIndex: number | null = 0;
  selectedMilestoneIndex: number | null = 0;
  editState: EditState | null = null;

  overviewValidationCallbacks: Array<() => void> = [];

  deleteReq: {
    type: 'phase' | 'milestone' | 'task-package';
    title: string;
    moveToList: { id: number; name: string }[];
    moveToId?: number;
    callback: (moveToId: number | null) => void;
  } | null = null;

  deletePopupVisible = false;

  disableClick: boolean = false;

  packageArchivePopupVisible = false;
  packageReactivatePopupVisible = false;
  packageArchiveImpactTasks: { taskId: number; taskTitle: string }[] = [];
  packagePendingArchive: TaskPackage | null = null;
  packagePendingReactivate: TaskPackage | null = null;

  @ViewChild(DxValidationGroupComponent) validationGroup!: DxValidationGroupComponent;

  constructor(
    private cdr: ChangeDetectorRef,
    private configService: ConfigService,
    private router: Router,
  ) {
    this.loadProjectConfiguration();
  }

  milestoneAdapterConfig = {
    getValue: () => {
      return this.overviewValidationCallback();
    },
    validationRequestsCallbacks: this.overviewValidationCallbacks,
  };

  async loadProjectConfiguration() {
    this.isLoading = true;

    try {
      const result = await this.configService.loadProjectConfiguration();

      if (result?.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else if (result) {
        this.phases = result.phases || [];
        // Reset selection to first phase if data was loaded
        if (this.phases.length > 0) {
          this.selectedPhaseIndex = 0;
          this.selectedMilestoneIndex = 0;
        } else {
          this.selectedPhaseIndex = null;
          this.selectedMilestoneIndex = null;
        }
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  async refreshConfiguration(selectedPhaseIndex: number = 0, selectedMilestoneIndex: number = 0) {
    this.isLoading = true;
    try {
      const result = await this.configService.loadProjectConfiguration(true);

      if (result?.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else if (result) {
        this.phases = result.phases || [];
        // Reset selection to first phase if data was loaded
        if (this.phases.length > 0 && this.selectedPhaseIndex !== null) {
          this.selectedPhaseIndex = selectedPhaseIndex;
          this.selectedMilestoneIndex = selectedMilestoneIndex;
        } else if (this.phases.length == 0) {
          this.selectedPhaseIndex = null;
          this.selectedMilestoneIndex = null;
        }
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  overviewValidationCallback = () => {
    // Collect all milestones from all phases
    const allMilestones: Milestone[] = [];
    for (const phase of this.phases) {
      if (phase.milestones?.length > 0) {
        allMilestones.push(...phase.milestones);
      }
    }

    // Check each milestone for validity
    for (const milestone of allMilestones) {
      // Check if milestone has a name
      if (!milestone.name || milestone.name.trim() === '') {
        return false;
      }

      // Check weighting range (0-100)
      if (milestone.weight < 0 || milestone.weight > 100) {
        return false;
      }

      // Check for duplicate names (case-insensitive)
      const duplicateExists = allMilestones.some(otherMilestone => {
        return (
          otherMilestone !== milestone &&
          otherMilestone.name &&
          otherMilestone.name.trim().toLowerCase() === milestone.name.trim().toLowerCase()
        );
      });

      if (duplicateExists) {
        return false;
      }
    }

    return true;
  };

  onPhaseClick(phaseIndex: number) {
    // Check if there's an invalid edit in progress
    if (this.editState) {
      return;
    }

    this.selectedPhaseIndex = phaseIndex;
    // Reset milestone selection when phase changes
    this.selectedMilestoneIndex = 0;
  }

  onMilestoneClick(milestoneIndex: number) {
    // Check if there's an invalid edit in progress
    if (this.editState) {
      return;
    }

    this.selectedMilestoneIndex = milestoneIndex;
  }

  get selectedPhase(): Phase | null {
    if (this.selectedPhaseIndex === null || !this.phases[this.selectedPhaseIndex]) {
      return null;
    }
    return this.phases[this.selectedPhaseIndex];
  }

  get selectedMilestone() {
    if (!this.selectedPhase || this.selectedMilestoneIndex === null) {
      return null;
    }
    const milestones = this.selectedPhase.milestones || [];
    return milestones[this.selectedMilestoneIndex] || null;
  }

  isEditing(type: string, editingIndex: number): boolean {
    return this.editState?.type === type && this.editState?.editingIndex === editingIndex;
  }

  startEdit(type: 'phase' | 'milestone' | 'task-package', editingIndex: number) {
    // Check if there's already an edit in progress and validate it first
    if (this.editState) {
      return;
    }

    this.editState = { type, editingIndex };
    this.cdr.detectChanges();
  }

  startAdd(type: 'phase' | 'milestone' | 'task-package', phaseIndex?: number, milestoneIndex?: number) {
    // Check if there's already an edit in progress and validate it first
    if (this.editState) {
      return;
    }

    // Add the item directly to the list with default values
    switch (type) {
      case 'phase':
        this.phases.push({
          id: null,
          title: '',
          showInGantt: true,
          sortingIndex: this.phases.length,
          milestones: [],
        });
        this.editState = {
          type,
          editingIndex: this.phases.length - 1,
          isNew: true,
        };
        break;
      case 'milestone':
        const phase = this.phases[phaseIndex!];
        if (phase) {
          if (!phase.milestones) phase.milestones = [];
          phase.milestones.push({
            id: null,
            name: '',
            weight: 0,
            phaseId: phase.id,
            taskPackages: [],
          });
          this.editState = {
            type,
            editingIndex: phase.milestones.length - 1,
            isNew: true,
          };
        }
        break;
      case 'task-package':
        const milestone = this.phases[phaseIndex!]?.milestones?.[milestoneIndex!];
        if (milestone) {
          if (!milestone.taskPackages) milestone.taskPackages = [];
          milestone.taskPackages.push({ id: null, name: '', milestoneId: milestone.id });
          this.editState = {
            type,
            editingIndex: milestone.taskPackages.length - 1,
            isNew: true,
          };
        }
        break;
    }
    this.cdr.detectChanges();
  }

  cancelEdit() {
    if (this.editState) {
      if (this.editState.isNew) {
        switch (this.editState.type) {
          case 'phase':
            this.phases.splice(this.editState.editingIndex, 1);
            break;
          case 'milestone':
            this.phases[this.selectedPhaseIndex!].milestones.splice(this.editState.editingIndex, 1);
            break;
          case 'task-package':
            this.phases[this.selectedPhaseIndex!].milestones[this.selectedMilestoneIndex!].taskPackages.splice(
              this.editState.editingIndex,
              1
            );
            break;
        }
      }
    }
    this.editState = null;
    this.cdr.detectChanges();
  }

  saveItem(editData: Phase | Milestone | TaskPackage, type: 'phase' | 'milestone' | 'task-package') {
    if (!this.validationGroup.instance.validate().isValid) return;

    const editIndex = this.editState.editingIndex;
    switch (type) {
      case 'phase':
        this.savePhase(editData as Phase, this.phases[editIndex]);
        break;
      case 'milestone':
        this.saveMilestone(editData as Milestone, this.phases[this.selectedPhaseIndex!].milestones[editIndex]);
        break;
      case 'task-package':
        this.saveTaskPackage(
          editData as TaskPackage,
          this.phases[this.selectedPhaseIndex!].milestones[this.selectedMilestoneIndex!].taskPackages[editIndex]
        );
        break;
    }
  }

  async savePhase(editData: Phase, phase: Phase) {
    try {
      Object.assign(phase, editData);

      const phaseIndex = this.phases.findIndex(x => x.id === phase.id);
      const payload = {
        phase: {
          ...phase,
          milestones: undefined,
          previousPhaseId: phaseIndex > 0 ? this.phases[phaseIndex - 1]?.id : null,
        },
      };

      const result = await this.configService.writePhase(payload);

      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }


      this.editState = null;
      this.refreshConfiguration(phaseIndex);
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  async saveMilestone(editData: Milestone, milestone: Milestone) {
    try {
      Object.assign(milestone, editData);

      const phase = this.phases.find(x => x.id === milestone.phaseId);
      if (!phase) {
        console.error('Phase not found for milestone');
        return;
      }

      const milestoneIndex = phase.milestones.findIndex(x => x.id === milestone.id);
      const payload = {
        milestone: {
          ...milestone,
          taskPackages: undefined,
          previousMilestoneId: milestoneIndex > 0 ? phase.milestones[milestoneIndex - 1]?.id : null,
        },
      };

      const result = await this.configService.writeMilestone(payload);

      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }


      this.editState = null;
      this.refreshConfiguration(this.selectedPhaseIndex, milestoneIndex);
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  async saveTaskPackage(editData: TaskPackage, taskPackage: TaskPackage) {
    try {
      Object.assign(taskPackage, editData);

      const phase = this.phases.find(x => x.milestones?.some(y => y.id === taskPackage.milestoneId));
      if (!phase) {
        console.error('Phase not found for task package');
        return;
      }

      const milestone = phase.milestones?.find(x => x.id === taskPackage.milestoneId);
      if (!milestone) {
        console.error('Milestone not found for task package');
        return;
      }

      const taskPackageIndex = milestone.taskPackages?.findIndex(x => x.id === taskPackage.id) || 0;
      const payload = {
        taskPackage: {
          ...taskPackage,
          previousTaskPackageId: taskPackageIndex > 0 ? milestone.taskPackages?.[taskPackageIndex - 1]?.id : null,
        },
      };

      const result = await this.configService.writeTaskPackage(payload);

      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }

      this.editState = null;
      this.refreshConfiguration(this.selectedPhaseIndex, this.selectedMilestoneIndex);
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  deleteItem(type: 'phase' | 'milestone' | 'task-package', editingIndex: number) {
    let moveToList: { id: number; name: string }[] = [];
    let item;
    switch (type) {
      case 'phase':
        item = this.phases[editingIndex];
        moveToList = this.phases.filter(x => x.id != item.id).map(x => ({ id: x.id, name: x.title }));
        break;
      case 'milestone':
        item = this.phases[this.selectedPhaseIndex]?.milestones[editingIndex];
        moveToList = this.phases
          .flatMap(x => x.milestones)
          .filter(x => x.id != item.id)
          .map(x => ({ id: x.id, name: x.name }));
        break;
      case 'task-package':
        item =
          this.phases[this.selectedPhaseIndex]?.milestones[this.selectedMilestoneIndex!]?.taskPackages[editingIndex];
        moveToList = this.phases
          .flatMap(x => x.milestones)
          .flatMap(x => x.taskPackages)
          .filter(x => x.id != item.id)
          .map(x => ({ id: x.id, name: x.name }));
        break;
    }

    this.deleteReq = {
      type,
      title: type === 'phase' ? item.title : item.name,
      moveToList,
      callback: (moveToId: number | null) => {
        switch (type) {
          case 'phase':
            this.savePhase({ ...item, isDeleted: true, moveToId }, item);
            break;
          case 'milestone':
            this.saveMilestone({ ...item, isDeleted: true, moveToId }, item);
            break;
          case 'task-package':
            this.saveTaskPackage({ ...item, isDeleted: true, moveToId }, item);
            break;
        }
        this.deleteReq = null;
        this.deletePopupVisible = false;
      },
    };
    this.deletePopupVisible = true;
    this.cdr.detectChanges();
  }

  get deletePopupTitle(): string {
    if (!this.deleteReq) return '';
    switch (this.deleteReq.type) {
      case 'phase': return $localize`:@@deletePhaseTitle:Delete Phase`;
      case 'milestone': return $localize`:@@deleteMilestoneTitle:Delete Milestone`;
      case 'task-package': return $localize`:@@deleteTaskPackageTitle:Delete Task Package`;
    }
  }

  get deleteWarningMessage(): string {
    if (!this.deleteReq) return '';
    const n = `<strong>${this.deleteReq.title}</strong>`;
    switch (this.deleteReq.type) {
      case 'phase':
        return $localize`:@@deletePhaseWarningWithName:All milestones and work packages within phase ${n}:PHASE_NAME: will be permanently deleted. This action cannot be undone.`;
      case 'milestone':
        return $localize`:@@deleteMilestoneWarningWithName:All work packages within milestone ${n}:MILESTONE_NAME: will be permanently deleted. This action cannot be undone.`;
      case 'task-package':
        return $localize`:@@deleteTaskPackageWarningWithName:All tasks within work package ${n}:TASK_PACKAGE_NAME: will be permanently deleted. This action cannot be undone.`;
    }
  }

  get deletePopupButtons(): PopupButton[] {
    if (!this.deleteReq) return [];
    const hasMoveTarget = !!this.deleteReq.moveToId;
    return [
      {
        text: $localize`:@@cancel:Cancel`,
        type: hasMoveTarget ? 'secondary' : 'danger-outline',
        onClick: () => { this.deleteReq = null; this.deletePopupVisible = false; },
      },
      {
        text: hasMoveTarget
          ? $localize`:@@moveAndDelete:Move & Delete`
          : $localize`:@@delete:Delete`,
        type: hasMoveTarget ? 'primary' : 'danger',
        onClick: () => { this.deleteReq.callback(this.deleteReq.moveToId ?? null); },
      },
    ];
  }

  onMoveToSelected(value: number | null) {
    if (this.deleteReq) {
      this.deleteReq.moveToId = value ?? undefined;
      this.cdr.detectChanges();
    }
  }

  onDeletePopupHiding() {
    this.deleteReq = null;
    this.deletePopupVisible = false;
  }

  startReorder(event: any) {
    const { fromIndex, fromData } = event;
    this.disableClick = true;
    event.itemData = fromData[fromIndex];
  }

  onDragMove(event: DragChangeEvent) {
    this.disableClick = true;
    event.cancel = false;
    const targetType = event.toComponent.element().getAttribute('type');
    const type = event.fromComponent.element().getAttribute('type');
    if (
      (event.dropInsideItem == true &&
        ((targetType != 'milestone' && type == 'task-package') ||
          (targetType != 'phase' && type == 'milestone') ||
          type == 'phase')) ||
      (event.dropInsideItem == false && targetType != type)
    ) {
      event.cancel = true;
      return;
    }
  }

  async onReorder(event: ReorderEvent) {
    const { fromIndex, toIndex, fromData, toData, itemData, dropInsideItem, toComponent, fromComponent } = event;
    const targetType = toComponent.element().getAttribute('type');
    const type = fromComponent.element().getAttribute('type');

    if (fromIndex === toIndex && !dropInsideItem) return;
    fromData.splice(fromIndex, 1);
    if (!dropInsideItem) {
      toData.splice(toIndex, 0, itemData);
    } else {
      switch (targetType) {
        case 'phase':
          toData[toIndex].milestones.push(itemData);
          itemData.phaseId = toData[toIndex].id;
          itemData.sortingIndex = toData[toIndex].milestones.length;
          break;
        case 'milestone':
          toData[toIndex].taskPackages.push(itemData);
          itemData.milestoneId = toData[toIndex].id;
          itemData.sortingIndex = toData[toIndex].taskPackages.length;
          break;
      }
    }

    switch (type) {
      case 'phase':
        this.savePhase(itemData, itemData);
        break;
      case 'milestone':
        this.saveMilestone(itemData, itemData);
        break;
      case 'task-package':
        this.saveTaskPackage(itemData, itemData);
        break;
    }
    
    this.cdr.detectChanges();
  }

  getPhaseValidationCallback(phaseIndex: number) {
    return (value: string) => {
      const trimmedName = value.trim().toLowerCase();
      if (!trimmedName) return false;

      return !this.phases.some(
        (phase, index) => index !== phaseIndex && phase.title?.trim()?.toLowerCase() === trimmedName
      );
    };
  }

  getMilestoneValidationCallback(milestoneIndex: number) {
    return (value: string) => {
      const trimmedName = value.trim().toLowerCase();
      if (!trimmedName) return false;

      // Check across all phases for unique milestone names
      return !this.phases.some(phase =>
        phase.milestones?.some((milestone, index) => {
          // Skip current milestone being edited
          if (phase === this.phases[this.selectedPhaseIndex] && index === milestoneIndex) {
            return false;
          }
          return milestone.name?.trim()?.toLowerCase() === trimmedName;
        })
      );
    };
  }

  getTaskValidationCallback(taskIndex: number) {
    return (value: string) => {
      const trimmedName = value.trim().toLowerCase();
      if (!trimmedName) return false;

      // Check across all phases and milestones for unique task package names
      return !this.phases.some(phase =>
        phase.milestones?.some(milestone =>
          milestone.taskPackages?.some((taskPackage, index) => {
            // Skip current task package being edited
            if (
              phase === this.phases[this.selectedPhaseIndex] &&
              milestone === phase.milestones![this.selectedMilestoneIndex] &&
              index === taskIndex
            ) {
              return false;
            }
            return taskPackage.name?.trim()?.toLowerCase() === trimmedName;
          })
        )
      );
    };
  }

  getNewItemValidationCallback(type: 'phase' | 'milestone' | 'task-package') {
    return (value: string) => {
      const trimmedName = value.trim().toLowerCase();
      if (!trimmedName) return false;

      switch (type) {
        case 'phase':
          return !this.phases.some(phase => phase.title?.trim()?.toLowerCase() === trimmedName);

        case 'milestone':
          return !this.phases.some(phase =>
            phase.milestones?.some(milestone => milestone.name?.trim()?.toLowerCase() === trimmedName)
          );

        case 'task-package':
          return !this.phases.some(phase =>
            phase.milestones?.some(milestone =>
              milestone.taskPackages?.some(taskPackage => taskPackage.name?.trim()?.toLowerCase() === trimmedName)
            )
          );

        default:
          return true;
      }
    };
  }

  onTaskPackageClick(taskPackage: TaskPackage) {
    // Check if there's an edit in progress
    
    if (this.disableClick){
      return;
    }
    if (this.editState) {
      return;
    }

    // Navigate to the task configuration page for this task package
    if (taskPackage.id) {
      this.router.navigate(['/config/project-config/configuration/tasks'], {
        queryParams: {
          packageId: taskPackage.id,
          milestoneId: this.phases[this.selectedPhaseIndex].milestones[this.selectedMilestoneIndex].id,
          phaseId: this.phases[this.selectedPhaseIndex].id,
        },
      });
    }
  }

  async onArchiveTaskPackage(pkg: TaskPackage): Promise<void> {
    const impact = await this.configService.getTaskPackageArchiveImpact(pkg.id);
    this.packageArchiveImpactTasks = impact?.affectedTasks ?? [];
    this.packagePendingArchive = pkg;
    this.packageArchivePopupVisible = true;
    this.cdr.detectChanges();
  }

  cancelArchivePackage(): void {
    this.packageArchivePopupVisible = false;
    this.packagePendingArchive = null;
  }

  async confirmArchiveTaskPackage(): Promise<void> {
    if (!this.packagePendingArchive?.id) return;
    await this.configService.archiveTaskPackage(this.packagePendingArchive.id);
    this.packageArchivePopupVisible = false;
    this.packagePendingArchive = null;
    this.refreshConfiguration(this.selectedPhaseIndex ?? 0, this.selectedMilestoneIndex ?? 0);
  }

  onReactivateTaskPackage(pkg: TaskPackage): void {
    this.packagePendingReactivate = pkg;
    this.packageReactivatePopupVisible = true;
    this.cdr.detectChanges();
  }

  cancelReactivatePackage(): void {
    this.packageReactivatePopupVisible = false;
    this.packagePendingReactivate = null;
  }

  async confirmReactivateTaskPackage(): Promise<void> {
    if (!this.packagePendingReactivate?.id) return;
    await this.configService.unarchiveTaskPackage(this.packagePendingReactivate.id);
    this.packageReactivatePopupVisible = false;
    this.packagePendingReactivate = null;
    this.refreshConfiguration(this.selectedPhaseIndex ?? 0, this.selectedMilestoneIndex ?? 0);
  }

  log(m) {
    console.log(m);
  }

  endDrag(){
    setTimeout(() => {
      this.disableClick = false;
    }, 600);
  }
}
